import { ReactNode } from "react";
import { deleteSkills, getSkills, postSkills, putSkills, Skills } from "../../ApiService/admin/Skill";
import { deletePortfolio, getPortfolio, Portfolio, postPortfolio, putPortfolio } from "../../ApiService/admin/Portfolio";
import { deleteExperience, Experience, getExperience, postExperience, putExperience } from "../../ApiService/admin/Experience";
import { deleteEducation, Educations, getEducation, postEducation, putEducation } from "../../ApiService/admin/Education";
import { deleteMessage, getMessages, markMessageRead, Message } from "../../ApiService/admin/Message";
import { Admin, createAdmin, deleteAdmin, getAdmins, updateAdmin } from "../../ApiService/admin/AdminResponse";
import { getCurrentAdmin } from "../../ApiService/auth";
import { imageUrl, PROJECT_PLACEHOLDER } from "../../utils/image";
import { AdminLang } from "./i18n";
import { formatDateTime, timeAgo } from "./time";

const SKILL_CATEGORIES = [
  { value: "Backend", label: "بک‌اند" },
  { value: "Frontend", label: "فرانت‌اند" },
  { value: "Tools", label: "ابزارها" },
];

const PROJECT_CATEGORIES = [
  { value: "Industrial", label: "صنعتی" },
  { value: "Backend", label: "بک‌اند" },
  { value: "Web", label: "وب" },
];

const labelOf = (options: { value: string; label: string }[], value: string) =>
  options.find((o) => o.value === value)?.label ?? value;

export interface FieldConfig {
  name: string;
  label: string;
  type: "text" | "number" | "textarea" | "url" | "email" | "password" | "select" | "checkbox" | "date" | "file";
  /** choices of a "select" field (labels are Persian keys for t()) */
  options?: { value: string; label: string }[];
  /** what a "file" field uploads */
  uploadKind?: "image" | "resume";
  required?: boolean;
  /** required only in the add form (e.g. a password that may stay unchanged on edit) */
  requiredOnCreate?: boolean;
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  ltr?: boolean;
  fullWidth?: boolean;
  hint?: string;
  /** hint shown instead of `hint` in the edit form */
  editHint?: string;
}

/** Passed to render functions so they can translate and format dates. */
export interface RenderContext {
  t: (fa: string) => string;
  lang: AdminLang;
}

export interface ColumnConfig<T> {
  header: string;
  render: (item: T, ctx: RenderContext) => ReactNode;
  wrap?: boolean;
}

export interface ResourceConfig<T extends { id: number }> {
  /** route segment under /admin */
  path: string;
  title: string;
  singular: string;
  icon: string;
  list: () => Promise<T[]>;
  create?: (data: Omit<T, "id">) => Promise<void>;
  update?: (id: number, data: Omit<T, "id">) => Promise<void>;
  remove?: (id: number) => Promise<void>;
  /** false hides the delete button of that row */
  canRemove?: (item: T) => boolean;
  /** "view" button that opens the whole item in a modal; `?view=<id>` in the url opens it directly */
  view?: {
    title: (item: T) => string;
    render: (item: T, ctx: RenderContext) => ReactNode;
    footer?: (item: T, ctx: RenderContext) => ReactNode;
    /** runs when the modal opens; the returned fields update the row */
    onOpen?: (item: T) => Promise<Partial<T> | void>;
  };
  rowClassName?: (item: T) => string | undefined;
  fields: FieldConfig[];
  columns: ColumnConfig<T>[];
  emptyText: string;
}

const clamp = (text: string, max = 80) => (text && text.length > max ? `${text.slice(0, max)}…` : text);

const skills: ResourceConfig<Skills> = {
  path: "skills",
  title: "مهارت‌ها",
  singular: "مهارت",
  icon: "ri-code-s-slash-line",
  list: getSkills,
  create: postSkills,
  update: putSkills,
  remove: deleteSkills,
  emptyText: "هنوز مهارتی ثبت نشده است.",
  fields: [
    { name: "name", label: "نام مهارت", type: "text", required: true, ltr: true },
    { name: "percentage", label: "درصد تسلط", type: "number", required: true, min: 0, max: 100 },
    { name: "category", label: "دسته‌بندی", type: "select", required: true, options: SKILL_CATEGORIES },
    { name: "icon", label: "متن آیکون", type: "text", ltr: true, hint: "حداکثر ۵ کاراکتر، مثلا C# یا SQL" },
    { name: "color", label: "رنگ کارت", type: "text", ltr: true, hint: "کد رنگ، مثلا #8b5cf6" },
  ],
  columns: [
    { header: "نام مهارت", render: (s) => <span dir="ltr">{s.name}</span> },
    { header: "دسته‌بندی", render: (s) => labelOf(SKILL_CATEGORIES, s.category) },
    {
      header: "درصد تسلط",
      render: (s) => (
        <div className="d-flex align-items-center gap-2 admin-progress-cell">
          <div className="progress progress-xs flex-fill">
            <div className="progress-bar bg-primary" style={{ width: `${s.percentage}%` }}></div>
          </div>
          <span className="fw-medium">{s.percentage}%</span>
        </div>
      ),
    },
  ],
};

const portfolio: ResourceConfig<Portfolio> = {
  path: "portfolio",
  title: "نمونه‌کارها",
  singular: "نمونه‌کار",
  icon: "ri-gallery-line",
  list: getPortfolio,
  create: postPortfolio,
  update: putPortfolio,
  remove: deletePortfolio,
  emptyText: "هنوز نمونه‌کاری ثبت نشده است.",
  fields: [
    { name: "name", label: "نام پروژه", type: "text", required: true },
    { name: "kind", label: "نوع پروژه", type: "text", hint: "برچسب بالای عنوان، مثلا مانیتورینگ صنعتی" },
    { name: "category", label: "دسته‌بندی", type: "select", required: true, options: PROJECT_CATEGORIES },
    { name: "isFeatured", label: "پروژه ویژه", type: "checkbox" },
    { name: "description", label: "توضیحات", type: "textarea", fullWidth: true },
    { name: "technologies", label: "تکنولوژی‌ها", type: "text", required: true, ltr: true, fullWidth: true, hint: "با کاما جدا کنید، مثلا .NET 8, OPC UA, SignalR" },
    { name: "webUrl", label: "لینک دمو", type: "url", ltr: true },
    { name: "githubUrl", label: "لینک کد (گیت‌هاب)", type: "url", ltr: true },
    { name: "assignedDate", label: "تاریخ شروع", type: "date", required: true },
    { name: "deliveryDate", label: "تاریخ تحویل", type: "date", required: true },
    { name: "progressPercentage", label: "درصد پیشرفت", type: "number", min: 0, max: 100, hint: "کمتر از ۱۰۰ یعنی پروژه در حال انجام است" },
    { name: "imageName", label: "تصویر پروژه", type: "file", uploadKind: "image", fullWidth: true },
  ],
  columns: [
    {
      header: "تصویر",
      render: (p) => (
        <span className="avatar avatar-md">
          <img src={imageUrl(p.imageName, PROJECT_PLACEHOLDER)} alt={p.name} />
        </span>
      ),
    },
    { header: "نام پروژه", render: (p) => p.name },
    { header: "دسته‌بندی", render: (p) => labelOf(PROJECT_CATEGORIES, p.category) },
    {
      header: "آدرس",
      render: (p) =>
        p.webUrl ? (
          <a href={p.webUrl} target="_blank" rel="noopener noreferrer" dir="ltr">{clamp(p.webUrl, 40)}</a>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
  ],
};

const experience: ResourceConfig<Experience> = {
  path: "experience",
  title: "سوابق کاری",
  singular: "سابقه کاری",
  icon: "ri-briefcase-4-line",
  list: getExperience,
  create: postExperience,
  update: putExperience,
  remove: deleteExperience,
  emptyText: "هنوز سابقه کاری ثبت نشده است.",
  fields: [
    { name: "title", label: "عنوان شغلی", type: "text", required: true },
    { name: "company", label: "شرکت / کارفرما", type: "text" },
    { name: "yearsActivity", label: "سال / مدت فعالیت", type: "text", required: true, hint: "همان‌طور که در سایت نمایش داده می‌شود، مثلا ۱۴۰۲ — اکنون" },
    { name: "isCurrent", label: "شغل فعلی", type: "checkbox" },
    { name: "tags", label: "تکنولوژی‌ها", type: "text", ltr: true, hint: "با کاما جدا کنید، مثلا .NET 8, OPC UA" },
    { name: "sortOrder", label: "ترتیب نمایش", type: "number", min: 0, hint: "عدد کمتر بالاتر نمایش داده می‌شود" },
    { name: "description", label: "توضیحات", type: "textarea", required: true, fullWidth: true },
  ],
  columns: [
    { header: "عنوان شغلی", render: (e) => e.title },
    { header: "شرکت / کارفرما", render: (e) => e.company || "—" },
    { header: "سال / مدت", render: (e) => <span className="badge bg-primary-transparent">{e.yearsActivity}</span> },
    { header: "توضیحات", render: (e) => clamp(e.description), wrap: true },
  ],
};

const education: ResourceConfig<Educations> = {
  path: "education",
  title: "سوابق تحصیلی",
  singular: "سابقه تحصیلی",
  icon: "ri-graduation-cap-line",
  list: getEducation,
  create: postEducation,
  update: putEducation,
  remove: deleteEducation,
  emptyText: "هنوز سابقه تحصیلی ثبت نشده است.",
  fields: [
    { name: "name", label: "رشته / مدرک", type: "text", required: true },
    { name: "institution", label: "دانشگاه / مؤسسه", type: "text", hint: "مثلا دانشگاه شاهرود · معدل ۱۸٫۲۰" },
    { name: "yearsStudy", label: "سال / مدت تحصیل", type: "text", required: true, hint: "مثلا ۱۳۹۵ — ۱۳۹۹ یا گواهینامه" },
    { name: "sortOrder", label: "ترتیب نمایش", type: "number", min: 0, hint: "عدد کمتر بالاتر نمایش داده می‌شود" },
    { name: "description", label: "توضیحات", type: "textarea", required: true, fullWidth: true },
  ],
  columns: [
    { header: "رشته / مدرک", render: (e) => e.name },
    { header: "دانشگاه / مؤسسه", render: (e) => e.institution || "—" },
    { header: "سال / مدت", render: (e) => <span className="badge bg-primary-transparent">{e.yearsStudy}</span> },
    { header: "توضیحات", render: (e) => clamp(e.description), wrap: true },
  ],
};

const messages: ResourceConfig<Message> = {
  path: "messages",
  title: "پیام‌های تماس",
  singular: "پیام",
  icon: "ri-mail-line",
  list: getMessages,
  remove: deleteMessage,
  emptyText: "پیامی از فرم تماس سایت دریافت نشده است.",
  fields: [],
  rowClassName: (m) => (m.isRead ? undefined : "admin-row-unread"),
  view: {
    title: (m) => m.subject,
    // opening the message clears its red dot in the header bell
    onOpen: async (m) => {
      if (m.isRead) return;
      await markMessageRead(m.id);
      return { isRead: true };
    },
    render: (m, { t, lang }) => (
      <>
        <ul className="list-unstyled admin-info-list mb-3">
          <li><span className="text-muted me-2">{t("نام")}:</span><span className="fw-medium">{m.name}</span></li>
          <li><span className="text-muted me-2">{t("ایمیل")}:</span><a href={`mailto:${m.email}`} dir="ltr">{m.email}</a></li>
          <li><span className="text-muted me-2">{t("تلفن")}:</span><a href={`tel:${m.phone}`} dir="ltr">{m.phone}</a></li>
          <li><span className="text-muted me-2">{t("تاریخ ارسال")}:</span>{formatDateTime(m.createDate, lang)}</li>
        </ul>
        <div className="p-3 rounded bg-light admin-message-text">{m.description}</div>
      </>
    ),
    footer: (m, { t }) => (
      <a href={`mailto:${m.email}?subject=${encodeURIComponent(`Re: ${m.subject}`)}`} className="btn btn-primary">
        <i className="ri-reply-line me-1 align-middle"></i>{t("پاسخ با ایمیل")}
      </a>
    ),
  },
  columns: [
    {
      header: "نام",
      render: (m, { t }) => (
        <>
          {m.name}
          {!m.isRead && <span className="badge bg-danger ms-2">{t("جدید")}</span>}
        </>
      ),
    },
    { header: "ایمیل", render: (m) => <a href={`mailto:${m.email}`} dir="ltr">{m.email}</a> },
    { header: "موضوع", render: (m) => m.subject, wrap: true },
    { header: "متن پیام", render: (m) => clamp(m.description, 60), wrap: true },
    { header: "تاریخ", render: (m, { lang }) => <span title={formatDateTime(m.createDate, lang)}>{timeAgo(m.createDate, lang)}</span> },
  ],
};

const users: ResourceConfig<Admin> = {
  path: "users",
  title: "کاربران",
  singular: "کاربر",
  icon: "ri-team-line",
  list: getAdmins,
  create: createAdmin,
  update: updateAdmin,
  remove: deleteAdmin,
  // you can't delete the account you are logged in with
  canRemove: (a) => a.id !== getCurrentAdmin()?.id,
  emptyText: "هنوز کاربری ثبت نشده است.",
  fields: [
    { name: "userName", label: "نام کاربری", type: "text", required: true, ltr: true, maxLength: 50 },
    { name: "email", label: "ایمیل", type: "email", required: true, ltr: true, maxLength: 100 },
    {
      name: "password",
      label: "رمز عبور",
      type: "password",
      requiredOnCreate: true,
      minLength: 8,
      ltr: true,
      hint: "حداقل ۸ کاراکتر",
      editHint: "برای تغییر ندادن رمز، خالی بگذارید. با تغییر رمز، آن کاربر از همه دستگاه‌ها خارج می‌شود.",
    },
  ],
  columns: [
    {
      header: "نام کاربری",
      render: (a, { t }) => (
        <>
          <span dir="ltr">{a.userName}</span>
          {a.id === getCurrentAdmin()?.id && <span className="badge bg-primary-transparent ms-2">{t("شما")}</span>}
        </>
      ),
    },
    { header: "ایمیل", render: (a) => <a href={`mailto:${a.email}`} dir="ltr">{a.email}</a> },
  ],
};

/** Everything the admin panel can manage besides "about me", in site order. */
export const RESOURCES = { skills, portfolio, experience, education, messages, users };
